import type { TaskDetail } from '@pm/schemas';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  LabelCaps,
  Select,
  Skeleton,
} from '@pm/ui';
import { Repeat } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useInvalidating, useRecurrence, wfKeys } from '@/lib/queries';
import { useToast } from '@/lib/toast';

type Preset = 'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY' | 'LAST_BUSINESS_DAY' | 'CUSTOM';
const DAYS = [
  ['MO', 'Seg'],
  ['TU', 'Ter'],
  ['WE', 'Qua'],
  ['TH', 'Qui'],
  ['FR', 'Sex'],
  ['SA', 'Sáb'],
  ['SU', 'Dom'],
] as const;

/** SPEC §19 — recorrência: presets comuns + RRULE customizada; gera novas instâncias (RULE-026). */
export function RecurrencePanel({ task }: { task: TaskDetail }) {
  const toast = useToast();
  const { data: rule, isLoading } = useRecurrence(task.id, !task.parent_id);
  const [open, setOpen] = useState(false);
  const inval = [wfKeys.recurrence(task.id)];
  const remove = useInvalidating(() => api.delete(`/tasks/${task.id}/recurrence`), inval);
  if (task.parent_id) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <LabelCaps>Recorrência</LabelCaps>
        {task.can.edit ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-6 text-xs"
            onClick={() => setOpen(true)}
          >
            <Repeat /> {rule ? 'Editar' : 'Configurar'}
          </Button>
        ) : null}
      </div>
      {isLoading ? (
        <Skeleton className="w-2/3" />
      ) : rule ? (
        <div className="text-sm">
          <div className="flex items-center gap-2">
            <Badge tone={rule.status === 'ACTIVE' ? 'success' : 'neutral'}>
              {rule.status === 'ACTIVE'
                ? 'ativa'
                : rule.status === 'PAUSED'
                  ? 'pausada'
                  : 'encerrada'}
            </Badge>
            <span className="font-mono text-xs text-text-3">{rule.rrule}</span>
          </div>
          <p className="mt-1 font-mono text-xs text-text-2">
            próxima: {fmtDateTime(rule.next_run_at)} · geradas: {rule.generated_count}
          </p>
          {task.can.edit ? (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-6 text-xs text-danger"
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => toast.success('Recorrência removida'),
                  onError: (e) => toast.error(e),
                })
              }
            >
              Remover
            </Button>
          ) : null}
        </div>
      ) : (
        <span className="text-sm text-text-3">Não se repete</span>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Recorrência"
          description="Cada ocorrência cria uma nova tarefa a partir desta; esta permanece como modelo."
          size="sm"
        >
          <RecurrenceForm
            taskId={task.id}
            initial={rule?.rrule ?? ''}
            initialAdjust={rule?.business_day_adjust ?? false}
            onDone={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecurrenceForm({
  taskId,
  initial,
  initialAdjust,
  onDone,
}: {
  taskId: string;
  initial: string;
  initialAdjust: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const [preset, setPreset] = useState<Preset>(initial ? 'CUSTOM' : 'WEEKLY');
  const [days, setDays] = useState<string[]>(['MO']);
  const [monthDay, setMonthDay] = useState('1');
  const [hour, setHour] = useState('09');
  const [custom, setCustom] = useState(initial);
  const [adjust, setAdjust] = useState(initialAdjust);
  const [preview, setPreview] = useState<string[] | null>(null);
  const save = useInvalidating(
    (input: { rrule: string; business_day_adjust: boolean }) =>
      api.put(`/tasks/${taskId}/recurrence`, {
        ...input,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
      }),
    [wfKeys.recurrence(taskId)],
  );

  const rrule = (() => {
    const h = `BYHOUR=${Number(hour)};BYMINUTE=0`;
    switch (preset) {
      case 'DAILY':
        return `FREQ=DAILY;${h}`;
      case 'WEEKDAYS':
        return `FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;${h}`;
      case 'WEEKLY':
        return `FREQ=WEEKLY;BYDAY=${days.join(',') || 'MO'};${h}`;
      case 'MONTHLY':
        return `FREQ=MONTHLY;BYMONTHDAY=${Number(monthDay)};${h}`;
      case 'LAST_BUSINESS_DAY':
        return `FREQ=MONTHLY;BYMONTHDAY=-1;${h}`;
      default:
        return custom;
    }
  })();

  async function doPreview() {
    try {
      const res = await api.post<{ occurrences: string[] }>('/recurrence/preview', { rrule });
      setPreview(res.occurrences);
    } catch (e) {
      toast.error(e);
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          { rrule, business_day_adjust: preset === 'LAST_BUSINESS_DAY' ? true : adjust },
          {
            onSuccess: () => (toast.success('Recorrência salva'), onDone()),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Frequência" htmlFor="rc-preset">
        <Select id="rc-preset" value={preset} onChange={(e) => setPreset(e.target.value as Preset)}>
          <option value="DAILY">Diariamente</option>
          <option value="WEEKDAYS">Dias úteis (seg–sex)</option>
          <option value="WEEKLY">Semanalmente</option>
          <option value="MONTHLY">Mensalmente, dia específico</option>
          <option value="LAST_BUSINESS_DAY">Último dia útil do mês</option>
          <option value="CUSTOM">Personalizada (RRULE)</option>
        </Select>
      </Field>
      {preset === 'WEEKLY' ? (
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map(([v, l]) => (
            <button
              key={v}
              type="button"
              onClick={() => setDays(days.includes(v) ? days.filter((d) => d !== v) : [...days, v])}
              className={`h-7 rounded border px-2 font-mono text-xs ${days.includes(v) ? 'border-accent-border bg-accent-soft text-accent' : 'border-border text-text-2'}`}
            >
              {l}
            </button>
          ))}
        </div>
      ) : null}
      {preset === 'MONTHLY' ? (
        <Field label="Dia do mês" htmlFor="rc-md">
          <Input
            id="rc-md"
            type="number"
            min={1}
            max={31}
            mono
            value={monthDay}
            onChange={(e) => setMonthDay(e.target.value)}
            className="w-24"
          />
        </Field>
      ) : null}
      {preset === 'CUSTOM' ? (
        <Field label="RRULE" htmlFor="rc-custom" hint="Ex.: FREQ=WEEKLY;INTERVAL=2;BYDAY=TU">
          <Input id="rc-custom" mono value={custom} onChange={(e) => setCustom(e.target.value)} />
        </Field>
      ) : null}
      {preset !== 'CUSTOM' ? (
        <Field label="Hora" htmlFor="rc-hour">
          <Input
            id="rc-hour"
            type="number"
            min={0}
            max={23}
            mono
            value={hour}
            onChange={(e) => setHour(e.target.value)}
            className="w-24"
          />
        </Field>
      ) : null}
      {preset !== 'LAST_BUSINESS_DAY' ? (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={adjust} onCheckedChange={(v) => setAdjust(v === true)} /> Se cair em
          fim de semana ou feriado, antecipar para o dia útil anterior
        </label>
      ) : null}
      <div className="rounded border border-border bg-surface-2 px-3 py-2 font-mono text-xs text-text-2">
        {rrule || '—'}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => void doPreview()}
      >
        Ver próximas ocorrências
      </Button>
      {preview ? (
        <ul className="font-mono text-xs text-text-2">
          {preview.map((p) => (
            <li key={p}>{fmtDateTime(p)}</li>
          ))}
          {preview.length === 0 ? (
            <li className="text-text-3">Nenhuma ocorrência futura.</li>
          ) : null}
        </ul>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={save.isPending} disabled={!rrule}>
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}
